/**
 * Workforce Assessments Page
 *
 * Manages workforce assessments including:
 * - Skills self-assessments (initial, periodic, exit)
 * - Training evaluations
 * - Certification exams with timed testing
 * - Compliance tracking dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  X,
  User,
  FileText,
  Play,
  Timer,
  Award,
  BarChart3,
  BookOpen,
  Target,
  TrendingUp,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

// Helper to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// ============================================================================
// INTERFACES
// ============================================================================

interface FormTemplate {
  id: number;
  name: string;
  description: string | null;
  form_type: string;
  is_active: boolean;
  version: number;
}

interface UserForm {
  id: number;
  template_name: string;
  form_type: string;
  status: string;
  completion_percentage: number;
  assigned_date: string | null;
  due_date: string | null;
  submitted_at: string | null;
  approved_at: string | null;
}

interface ComplianceItem {
  rule_id: number;
  rule_name: string;
  compliance_type: string;
  frequency: string;
  is_mandatory: boolean;
  status: string;
  last_completed: string | null;
  next_due: string | null;
  days_until_due: number | null;
  is_overdue: boolean;
}

interface UserCompliance {
  user_id: number;
  compliance_score: number;
  is_fully_compliant: boolean;
  summary: {
    compliant: number;
    pending: number;
    overdue: number;
    not_applicable: number;
  };
  total_requirements: number;
  items: ComplianceItem[];
}

interface ExamQuestion {
  id: string;
  question: string;
  type: 'multiple_choice' | 'true_false' | 'short_answer';
  options?: string[];
  required: boolean;
}

interface ExamState {
  form_instance_id: number;
  status: string;
  time_limit_minutes: number | null;
  started_at: string | null;
  time_remaining_minutes: number | null;
  questions: ExamQuestion[];
  answers: Record<string, string>;
}

interface ExamResult {
  form_instance_id: number;
  status: string;
  score: number;
  passed: boolean;
  passing_score: number;
  questions_correct: number;
  questions_total: number;
  time_taken_minutes: number | null;
  feedback: string | null;
}

interface RosterUser {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  role_name: string;
}

// ============================================================================
// STATUS CONFIGURATIONS
// ============================================================================

const FORM_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Draft', color: 'text-ink-700', bgColor: 'bg-paper-2' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  submitted: { label: 'Submitted', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  approved: { label: 'Approved', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  rejected: { label: 'Rejected', color: 'text-red-700', bgColor: 'bg-red-100' },
  scored: { label: 'Scored', color: 'text-purple-700', bgColor: 'bg-purple-100' },
};

const COMPLIANCE_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  compliant: { label: 'Compliant', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  pending: { label: 'Pending', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  overdue: { label: 'Overdue', color: 'text-red-700', bgColor: 'bg-red-100' },
  not_applicable: { label: 'N/A', color: 'text-ink-500', bgColor: 'bg-paper-2' },
};

// ============================================================================
// EXAM TAKING MODAL COMPONENT
// ============================================================================

function ExamTakingModal({
  isOpen,
  onClose,
  examState,
  onAnswer,
  onSubmit,
  submitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  examState: ExamState;
  onAnswer: (questionId: string, answer: string) => void;
  onSubmit: () => void;
  submitting: boolean;
}) {
  const [timeRemaining, setTimeRemaining] = useState(examState.time_remaining_minutes || 0);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Timer countdown
  useEffect(() => {
    if (!isOpen || examState.status !== 'in_progress') return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          onSubmit(); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1 / 60; // Decrement by 1 second
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, examState.status, onSubmit]);

  // Update time remaining when exam state changes
  useEffect(() => {
    if (examState.time_remaining_minutes !== null) {
      setTimeRemaining(examState.time_remaining_minutes);
    }
  }, [examState.time_remaining_minutes]);

  if (!isOpen) return null;

  const formatTime = (minutes: number) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const questions = examState.questions || [];
  const question = questions[currentQuestion];
  const answeredCount = Object.keys(examState.answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  const isTimeWarning = timeRemaining <= 5;
  const isTimeCritical = timeRemaining <= 2;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-4 md:inset-8 bg-surface rounded-xl shadow-2xl flex flex-col">
        {/* Exam Header */}
        <div className="px-6 py-4 border-b border-v2border flex items-center justify-between bg-paper">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-ink">Certification Exam</h2>
              <p className="text-sm text-ink-500">
                Question {currentQuestion + 1} of {questions.length}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Timer */}
            <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
              isTimeCritical ? 'bg-red-100 text-red-700' :
              isTimeWarning ? 'bg-amber-100 text-amber-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              <Timer className="w-5 h-5" />
              <span className="font-mono font-bold text-lg">{formatTime(timeRemaining)}</span>
            </div>
            {/* Progress */}
            <div className="hidden md:flex items-center space-x-2">
              <span className="text-sm text-ink-500">{answeredCount}/{questions.length} answered</span>
              <div className="w-24 h-2 bg-paper-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {question ? (
            <div className="max-w-3xl mx-auto">
              {/* Question */}
              <div className="mb-8">
                <div className="flex items-start space-x-3 mb-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold">
                    {currentQuestion + 1}
                  </span>
                  <p className="text-lg text-ink pt-1">{question.question}</p>
                </div>
                {question.required && (
                  <span className="text-xs text-red-500 ml-11">* Required</span>
                )}
              </div>

              {/* Answer Options */}
              <div className="space-y-3 ml-11">
                {question.type === 'multiple_choice' && question.options?.map((option, idx) => (
                  <label
                    key={idx}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      examState.answers[question.id] === option
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-v2border hover:border-purple-300 hover:bg-paper'
                    }`}
                  >
                    <input
                      type="radio"
                      name={question.id}
                      value={option}
                      checked={examState.answers[question.id] === option}
                      onChange={() => onAnswer(question.id, option)}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-3 text-ink">{option}</span>
                  </label>
                ))}

                {question.type === 'true_false' && (
                  <div className="flex space-x-4">
                    {['True', 'False'].map((option) => (
                      <label
                        key={option}
                        className={`flex-1 flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          examState.answers[question.id] === option
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-v2border hover:border-purple-300 hover:bg-paper'
                        }`}
                      >
                        <input
                          type="radio"
                          name={question.id}
                          value={option}
                          checked={examState.answers[question.id] === option}
                          onChange={() => onAnswer(question.id, option)}
                          className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-ink font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'short_answer' && (
                  <textarea
                    value={examState.answers[question.id] || ''}
                    onChange={(e) => onAnswer(question.id, e.target.value)}
                    placeholder="Enter your answer..."
                    rows={4}
                    className="w-full px-4 py-3 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ClipboardCheck className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <p className="text-ink-500">No questions available</p>
            </div>
          )}
        </div>

        {/* Question Navigation */}
        <div className="px-6 py-4 border-t border-v2border bg-paper">
          {/* Question Pills */}
          <div className="flex flex-wrap gap-2 mb-4 justify-center">
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(idx)}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                  idx === currentQuestion
                    ? 'bg-purple-600 text-white'
                    : examState.answers[q.id]
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-paper-2 text-ink-500 hover:bg-gray-300'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
              className="px-4 py-2 border border-v2border rounded-lg text-ink-700 hover:bg-paper-2 disabled:opacity-50"
            >
              Previous
            </button>
            <div className="flex items-center space-x-3">
              {currentQuestion < questions.length - 1 ? (
                <button
                  onClick={() => setCurrentQuestion((prev) => Math.min(questions.length - 1, prev + 1))}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={onSubmit}
                  disabled={submitting || answeredCount < questions.filter(q => q.required).length}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Submit Exam</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EXAM RESULTS MODAL
// ============================================================================

function ExamResultsModal({
  isOpen,
  onClose,
  result,
}: {
  isOpen: boolean;
  onClose: () => void;
  result: ExamResult | null;
}) {
  if (!isOpen || !result) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="text-center mb-6">
            {result.passed ? (
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            )}
            <h2 className={`font-display text-2xl font-semibold ${result.passed ? 'text-emerald-600' : 'text-red-600'}`}>
              {result.passed ? 'Congratulations!' : 'Not Passed'}
            </h2>
            <p className="text-ink-500 mt-1">
              {result.passed
                ? 'You have passed the certification exam!'
                : 'You did not meet the passing score. You may retake the exam.'}
            </p>
          </div>

          <div className="bg-paper rounded-xl p-4 space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-500">Your Score</span>
              <span className={`font-display text-2xl font-semibold ${result.passed ? 'text-emerald-600' : 'text-red-600'}`}>
                {result.score.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-500">Passing Score</span>
              <span className="font-medium text-ink">{result.passing_score}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-500">Questions Correct</span>
              <span className="font-medium text-ink">
                {result.questions_correct} / {result.questions_total}
              </span>
            </div>
            {result.time_taken_minutes && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink-500">Time Taken</span>
                <span className="font-medium text-ink">{result.time_taken_minutes} minutes</span>
              </div>
            )}
          </div>

          {result.feedback && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-blue-700">{result.feedback}</p>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-paper-2 text-ink-700 rounded-lg hover:bg-paper-2 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CREATE ASSESSMENT MODAL
// ============================================================================

function CreateAssessmentModal({
  isOpen,
  onClose,
  users,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  users: RosterUser[];
  onCreated: () => void;
}) {
  const [assessmentType, setAssessmentType] = useState<'skills' | 'training'>('skills');
  const [userId, setUserId] = useState('');
  const [skillsType, setSkillsType] = useState('initial');
  const [curriculumEnrollmentId, setCurriculumEnrollmentId] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const token = getAuthToken();
      let endpoint = '';
      let body = {};

      if (assessmentType === 'skills') {
        endpoint = '/api/workforce/forms/skills-assessment';
        body = {
          user_id: parseInt(userId),
          assessment_type: skillsType,
        };
      } else {
        endpoint = '/api/workforce/forms/training-evaluation';
        body = {
          curriculum_enrollment_id: parseInt(curriculumEnrollmentId),
          module_name: moduleName,
          user_id: parseInt(userId),
        };
      }

      const response = await authFetch(getWeaveinApiUrl(endpoint), {
        method: 'POST',
        jsonBody: body,
      });

      if (response.ok) {
        onClose();
        onCreated();
        // Reset form
        setUserId('');
        setSkillsType('initial');
        setCurriculumEnrollmentId('');
        setModuleName('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create assessment');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-ink">Create Assessment</h2>
            <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Assessment Type Toggle */}
            <div className="flex rounded-lg bg-paper-2 p-1">
              <button
                type="button"
                onClick={() => setAssessmentType('skills')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  assessmentType === 'skills'
                    ? 'bg-surface text-ink shadow'
                    : 'text-ink-500 hover:text-ink'
                }`}
              >
                Skills Assessment
              </button>
              <button
                type="button"
                onClick={() => setAssessmentType('training')}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  assessmentType === 'training'
                    ? 'bg-surface text-ink shadow'
                    : 'text-ink-500 hover:text-ink'
                }`}
              >
                Training Evaluation
              </button>
            </div>

            {/* User Selection */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">User *</label>
              <select
                required
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select a user...</option>
                {users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.user_name} ({user.role_name})
                  </option>
                ))}
              </select>
            </div>

            {/* Skills Assessment Options */}
            {assessmentType === 'skills' && (
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Assessment Type *</label>
                <select
                  required
                  value={skillsType}
                  onChange={(e) => setSkillsType(e.target.value)}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="initial">Initial (New Hire)</option>
                  <option value="periodic">Periodic (Check-in)</option>
                  <option value="exit">Exit (Leaving Role)</option>
                </select>
              </div>
            )}

            {/* Training Evaluation Options */}
            {assessmentType === 'training' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Curriculum Enrollment ID *</label>
                  <input
                    type="number"
                    required
                    value={curriculumEnrollmentId}
                    onChange={(e) => setCurriculumEnrollmentId(e.target.value)}
                    placeholder="Enter enrollment ID"
                    className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">Module Name *</label>
                  <input
                    type="text"
                    required
                    value={moduleName}
                    onChange={(e) => setModuleName(e.target.value)}
                    placeholder="e.g., Communication Skills Module"
                    className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export default function WorkforceAssessmentsPage() {
  const { t } = useAppTranslation();
  const [activeTab, setActiveTab] = useState<'assessments' | 'compliance'>('assessments');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Data states
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [userForms, setUserForms] = useState<UserForm[]>([]);
  const [pendingForms, setPendingForms] = useState<UserForm[]>([]);
  const [userCompliance, setUserCompliance] = useState<UserCompliance | null>(null);
  const [rosterUsers, setRosterUsers] = useState<RosterUser[]>([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [examState, setExamState] = useState<ExamState | null>(null);
  const [examResult, setExamResult] = useState<ExamResult | null>(null);
  const [showExamModal, setShowExamModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [examSubmitting, setExamSubmitting] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [templatesRes, myFormsRes, pendingRes, complianceRes, rosterRes] = await Promise.all([
        authFetch(getWeaveinApiUrl('/api/workforce/forms/templates')),
        authFetch(getWeaveinApiUrl('/api/workforce/forms/my-forms')),
        authFetch(getWeaveinApiUrl('/api/workforce/forms/pending')),
        authFetch(getWeaveinApiUrl('/api/workforce/compliance/my-status')),
        authFetch(getWeaveinApiUrl('/api/workforce/roster?limit=200')),
      ]);

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setFormTemplates(data || []);
      }

      if (myFormsRes.ok) {
        const data = await myFormsRes.json();
        setUserForms(data || []);
      }

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingForms(data.items || []);
      }

      if (complianceRes.ok) {
        const data = await complianceRes.json();
        setUserCompliance(data);
      }

      if (rosterRes.ok) {
        const data = await rosterRes.json();
        setRosterUsers(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Start exam
  const handleStartExam = async (formInstanceId: number) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/forms/exam/start'), {
        method: 'POST',
        jsonBody: { form_instance_id: formInstanceId },
      });

      if (response.ok) {
        const data = await response.json();
        // Mock questions for demo - in real app, fetch from form fields
        setExamState({
          form_instance_id: data.form_instance_id,
          status: data.status,
          time_limit_minutes: data.time_limit_minutes,
          started_at: data.started_at,
          time_remaining_minutes: data.time_remaining_minutes,
          questions: [
            {
              id: 'q1',
              question: 'What is the primary role of a Community Health Worker?',
              type: 'multiple_choice',
              options: [
                'Prescribing medications',
                'Connecting communities to health services',
                'Performing surgeries',
                'Managing hospital operations',
              ],
              required: true,
            },
            {
              id: 'q2',
              question: 'HIPAA compliance is optional for healthcare workers.',
              type: 'true_false',
              required: true,
            },
            {
              id: 'q3',
              question: 'List three key competencies for effective community health work.',
              type: 'short_answer',
              required: true,
            },
          ],
          answers: {},
        });
        setShowExamModal(true);
      }
    } catch (error) {
      console.error('Error starting exam:', error);
    }
  };

  // Handle exam answer
  const handleExamAnswer = useCallback((questionId: string, answer: string) => {
    setExamState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        answers: {
          ...prev.answers,
          [questionId]: answer,
        },
      };
    });
  }, []);

  // Submit exam
  const handleSubmitExam = useCallback(async () => {
    if (!examState) return;

    const token = getAuthToken();
    if (!token) return;

    setExamSubmitting(true);

    try {
      const response = await authFetch(getWeaveinApiUrl('/api/workforce/forms/exam/submit'), {
        method: 'POST',
        jsonBody: {
          form_instance_id: examState.form_instance_id,
          answers: examState.answers,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setExamResult(result);
        setShowExamModal(false);
        setShowResultsModal(true);
        setExamState(null);
        fetchData(); // Refresh data
      }
    } catch (error) {
      console.error('Error submitting exam:', error);
    } finally {
      setExamSubmitting(false);
    }
  }, [examState]);

  // Filter forms
  const filteredForms = userForms.filter((form) => {
    const matchesSearch =
      form.template_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || form.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Stats
  const stats = {
    totalAssessments: userForms.length,
    pending: userForms.filter((f) => f.status === 'draft' || f.status === 'in_progress').length,
    completed: userForms.filter((f) => f.status === 'approved' || f.status === 'scored').length,
    complianceScore: userCompliance?.compliance_score || 0,
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Assessments"
        title={t('workforce.assessments.title', 'Assessments & Compliance')}
        description={t('workforce.assessments.description', 'Skills assessments, training evaluations, and compliance tracking')}
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            <span>New Assessment</span>
          </button>
        }
      />
      <PageContainer>
      <div className="space-y-6">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Total Assessments</p>
                <p className="font-display text-2xl font-semibold text-ink">{stats.totalAssessments}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Pending</p>
                <p className="font-display text-2xl font-semibold text-amber-600">{stats.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Completed</p>
                <p className="font-display text-2xl font-semibold text-emerald-600">{stats.completed}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Compliance Score</p>
                <p className={`font-display text-2xl font-semibold ${
                  stats.complianceScore >= 80 ? 'text-emerald-600' :
                  stats.complianceScore >= 60 ? 'text-amber-600' :
                  'text-red-600'
                }`}>
                  {stats.complianceScore.toFixed(0)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-v2border">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('assessments')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'assessments'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <ClipboardCheck className="w-4 h-4" />
                <span>My Assessments</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('compliance')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'compliance'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-ink-500 hover:text-ink-700'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Target className="w-4 h-4" />
                <span>Compliance Status</span>
                {userCompliance && !userCompliance.is_fully_compliant && (
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Assessments Tab Content */}
        {activeTab === 'assessments' && (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Statuses</option>
                {Object.entries(FORM_STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Assessments List */}
            <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-paper border-b border-v2border">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Assessment</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Type</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Progress</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Due Date</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-ink-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-ink-500">
                          Loading assessments...
                        </td>
                      </tr>
                    ) : filteredForms.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <ClipboardCheck className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                          <p className="text-ink-500">No assessments found</p>
                          <p className="text-sm text-ink-500 mt-1">Create your first assessment to get started</p>
                        </td>
                      </tr>
                    ) : (
                      filteredForms.map((form) => {
                        const statusConfig = FORM_STATUS_CONFIG[form.status] || FORM_STATUS_CONFIG.draft;
                        const isExam = form.form_type === 'exam';
                        const canTakeExam = isExam && (form.status === 'draft' || form.status === 'in_progress');

                        return (
                          <tr key={form.id} className="hover:bg-paper">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${
                                  isExam ? 'bg-purple-100' : 'bg-blue-100'
                                }`}>
                                  {isExam ? (
                                    <Award className="w-5 h-5 text-purple-600" />
                                  ) : (
                                    <FileText className="w-5 h-5 text-blue-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-ink">{form.template_name}</p>
                                  <p className="text-sm text-ink-500">ID: {form.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-ink-500 capitalize">
                                {form.form_type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <div className="w-24 h-2 bg-paper-2 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500"
                                    style={{ width: `${form.completion_percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-ink-500">{form.completion_percentage}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {form.due_date ? (
                                <span className="text-sm text-ink-500">
                                  {new Date(form.due_date).toLocaleDateString()}
                                </span>
                              ) : (
                                <span className="text-sm text-ink-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {canTakeExam && (
                                <button
                                  onClick={() => handleStartExam(form.id)}
                                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm flex items-center space-x-1"
                                >
                                  <Play className="w-3 h-3" />
                                  <span>Take Exam</span>
                                </button>
                              )}
                              {!canTakeExam && (
                                <button className="p-1.5 text-ink-500 hover:bg-paper-2 rounded">
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Compliance Tab Content */}
        {activeTab === 'compliance' && userCompliance && (
          <div className="space-y-6">
            {/* Compliance Summary */}
            <div className="bg-surface rounded-xl border border-v2border p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-ink">Compliance Overview</h3>
                  <p className="text-sm text-ink-500">
                    {userCompliance.is_fully_compliant
                      ? 'You are fully compliant with all requirements'
                      : 'Some requirements need attention'}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-lg ${
                  userCompliance.is_fully_compliant
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  <span className="font-display text-2xl font-semibold">{userCompliance.compliance_score.toFixed(0)}%</span>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="font-display text-2xl font-semibold text-emerald-600">{userCompliance.summary.compliant}</p>
                  <p className="text-xs text-emerald-700">Compliant</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="font-display text-2xl font-semibold text-amber-600">{userCompliance.summary.pending}</p>
                  <p className="text-xs text-amber-700">Pending</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="font-display text-2xl font-semibold text-red-600">{userCompliance.summary.overdue}</p>
                  <p className="text-xs text-red-700">Overdue</p>
                </div>
                <div className="text-center p-3 bg-paper rounded-lg">
                  <p className="font-display text-2xl font-semibold text-ink-500">{userCompliance.summary.not_applicable}</p>
                  <p className="text-xs text-ink-700">N/A</p>
                </div>
              </div>
            </div>

            {/* Overdue Alert */}
            {userCompliance.summary.overdue > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">
                      {userCompliance.summary.overdue} requirement(s) are overdue
                    </p>
                    <p className="text-sm text-red-600">Complete these as soon as possible to maintain compliance</p>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Items */}
            <div className="bg-surface rounded-xl border border-v2border overflow-hidden">
              <div className="px-6 py-4 border-b border-v2border">
                <h3 className="font-semibold text-ink">Requirements ({userCompliance.total_requirements})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {userCompliance.items.map((item) => {
                  const statusConfig = COMPLIANCE_STATUS_CONFIG[item.status] || COMPLIANCE_STATUS_CONFIG.pending;

                  return (
                    <div key={item.rule_id} className="px-6 py-4 flex items-center justify-between hover:bg-paper">
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-lg ${
                          item.compliance_type === 'certification' ? 'bg-purple-100' :
                          item.compliance_type === 'training' ? 'bg-blue-100' :
                          item.compliance_type === 'assessment' ? 'bg-amber-100' :
                          'bg-paper-2'
                        }`}>
                          {item.compliance_type === 'certification' && <Award className="w-5 h-5 text-purple-600" />}
                          {item.compliance_type === 'training' && <BookOpen className="w-5 h-5 text-blue-600" />}
                          {item.compliance_type === 'assessment' && <ClipboardCheck className="w-5 h-5 text-amber-600" />}
                          {!['certification', 'training', 'assessment'].includes(item.compliance_type) && (
                            <FileText className="w-5 h-5 text-ink-500" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-ink">{item.rule_name}</p>
                          <div className="flex items-center space-x-2 text-sm text-ink-500">
                            <span className="capitalize">{item.compliance_type.replace('_', ' ')}</span>
                            <span>•</span>
                            <span className="capitalize">{item.frequency}</span>
                            {item.is_mandatory && (
                              <>
                                <span>•</span>
                                <span className="text-red-500">Required</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          {item.next_due && (
                            <p className={`text-sm ${
                              item.is_overdue ? 'text-red-600 font-medium' :
                              (item.days_until_due !== null && item.days_until_due <= 30) ? 'text-amber-600' :
                              'text-ink-500'
                            }`}>
                              {item.is_overdue ? 'Overdue' : `Due ${new Date(item.next_due).toLocaleDateString()}`}
                            </p>
                          )}
                          {item.last_completed && (
                            <p className="text-xs text-ink-400">
                              Last: {new Date(item.last_completed).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <CreateAssessmentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          users={rosterUsers}
          onCreated={fetchData}
        />

        {examState && (
          <ExamTakingModal
            isOpen={showExamModal}
            onClose={() => {
              setShowExamModal(false);
              setExamState(null);
            }}
            examState={examState}
            onAnswer={handleExamAnswer}
            onSubmit={handleSubmitExam}
            submitting={examSubmitting}
          />
        )}

        <ExamResultsModal
          isOpen={showResultsModal}
          onClose={() => {
            setShowResultsModal(false);
            setExamResult(null);
          }}
          result={examResult}
        />
      </div>
      </PageContainer>
    </div>
  );
}
